import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Grid, Group, LoadingOverlay } from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { MainLayout } from '../../layouts'
import { ContentBox, BackListButton, SaveButton, ResetButton } from '../../partials'
import { FormTypes } from '../../../types'
import { useAppDispatch, useAppSelector } from '../../../store'
import { IEmployeeParams } from '../../../store/types'
import { IFormRef } from '../../../types'
import { getFormattedValidationErrors } from '../../../utils/mixins'
import Form from './Form'
import { fetchShowEmployee, resetShowEmployee } from '../../../store/features/employees/showEmployeeSlice'
import { fetchUpdateEmployee, resetUpdateEmployee } from '../../../store/features/employees/updateEmployeeSlice'

const Edit = () => {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const formRef = useRef<IFormRef>(null)
  const dispatch = useAppDispatch()
  const [data, setData] = useState<IEmployeeParams | null>(null)

  const {
    isLoading: userIsLoading,
    response: userResponse,
    error: userError
  } = useAppSelector(state => state.showEmployee)
  const { isLoading, response, error } = useAppSelector(state => state.updateEmployee)

  useEffect(() => {
    if (userResponse === null && data === null) {
      ;(async () => {
        await dispatch(fetchShowEmployee(id as string))
      })()
    }
  }, [userResponse, data, id, dispatch])

  const getSerializedData = (data: any) => {
    return {
      company: data.company.id,
      user: data.user.id,
      is_admin: data.is_admin
    }
  }

  useEffect(() => {
    if (userResponse !== null) {
      setData(getSerializedData(userResponse.data))

      if (data !== null) {
        dispatch(resetShowEmployee())
      }
    }
  }, [userResponse, data, dispatch])

  useEffect(() => {
    if (userError !== null) {
      if (userError.response.status === 401 && id) {
        ;(async () => {
          dispatch(resetShowEmployee())
          await dispatch(fetchShowEmployee(id))
        })()
      } else if (userError.response.status === 404) {
        dispatch(resetShowEmployee())
        showNotification({
          color: 'red',
          title: t('error'),
          message: t(userError.data.message)
        })

        navigate('/employees')
      } else {
        dispatch(resetShowEmployee())
        showNotification({
          color: 'red',
          title: t('error'),
          message: t('errors.unknown')
        })

        navigate('/employees')
      }
    }
  }, [userError, id, dispatch, t, navigate])

  useEffect(() => {
    if (response !== null) {
      setData(getSerializedData(response.data))

      showNotification({
        color: 'green',
        title: t('successful'),
        message: t('updated')
      })

      dispatch(resetUpdateEmployee())
    }
  }, [response, t, dispatch])

  useEffect(() => {
    if (error !== null) {
      if (error.response.status === 422) {
        formRef.current!.setErrors(getFormattedValidationErrors(error))
      } else {
        showNotification({
          color: 'red',
          title: t('unsuccessful'),
          message: error.response.data ? error.response.data.message : t('errors.unknown')
        })
      }
    }
  }, [error, t])

  useEffect(() => {
    if (response !== null) {
      dispatch(resetUpdateEmployee())
      formRef.current!.reset()
    }
  }, [response, dispatch])

  const handleSubmit = async (params: IEmployeeParams) => {
    await dispatch(fetchUpdateEmployee({ id: id as string, params }))
  }

  return (
    <MainLayout>
      <LoadingOverlay visible={userIsLoading || isLoading || !data} />
      {data && (
        <ContentBox title={t('content_actions.create')}>
          <Form
            id="edit-employee-form"
            type={FormTypes.EDIT}
            ref={formRef}
            initialValues={data}
            onSubmit={handleSubmit}
          />
        </ContentBox>
      )}
      <ContentBox>
        <Grid>
          <Grid.Col span={6}>
            <BackListButton to="/employees" />
          </Grid.Col>
          <Grid.Col span={6}>
            <Group position="right">
              <SaveButton form="edit-employee-form" />
              <ResetButton form="edit-employee-form" />
            </Group>
          </Grid.Col>
        </Grid>
      </ContentBox>
    </MainLayout>
  )
}

export default Edit
